import {
  Link,
  Page,
  Document as PdfDocument,
  Image as PdfImage,
  StyleSheet,
  Text,
  View,
} from "@react-pdf/renderer";
import { marked } from "marked";
import React from "react";
import { preprocessFootnotes } from "~/utils/markdownFootnotes";
import {
  ensureBoldHeadingSpacing,
  ensureMarkdownNewlines,
  normalizeLiteralNewlines,
} from "~/utils/markdownTerms";
import { getExportStyles } from "./markdown-converters";

type PdfCaseInfo = {
  caseName: string;
  dateOfBirth: string;
  dateOfLoss: string;
  claimNumber: string;
};

type PdfDocumentOptions = {
  title: string;
  includeTitle?: boolean;
  titleAlignment?: "left" | "center";
  showHeader?: boolean;
  caseInfo?: PdfCaseInfo;
  includeFooter?: boolean;
  includeLogo?: boolean;
  logoSrc?: string;
  fontFamily?: string;
  lineSpacing?: string;
  footnote?: string | null;
};

export const logoDataUrl = `data:image/svg+xml;base64,${btoa(`<svg width="180" height="70" viewBox="0 0 180 70" fill="none" xmlns="http://www.w3.org/2000/svg">
<g clip-path="url(#clip0_9115_3108)">
<g clip-path="url(#clip1_9115_3108)">
<g clip-path="url(#paint0_angular_9115_3108_clip_path)" data-figma-skip-parse="true"><g transform="matrix(0.0181491 -0.0226863 0.0219266 0.0175412 26.1106 43.75)"><foreignObject x="-1032.36" y="-1032.36" width="2064.71" height="2064.71"><div xmlns="http://www.w3.org/1999/xhtml" style="background:conic-gradient(from 90deg,rgba(131, 237, 203, 0) 0deg,rgba(131, 237, 203, 0.7) 126deg,rgba(78, 187, 152, 1) 234deg,rgba(2, 112, 77, 1) 360deg);height:100%;width:100%;opacity:1"></div></foreignObject></g></g><path d="M44.2597 61.8991C54.2832 51.8756 54.2832 35.6244 44.2597 25.6009L26.1106 43.75C21.2972 38.9366 18.5931 32.4082 18.593 25.6009C18.593 23.3365 18.8923 21.103 19.4688 18.9532C15.2534 20.078 11.2685 22.294 7.96157 25.6009C-2.06189 35.6244 -2.06189 51.8756 7.96157 61.8991C17.985 71.9225 34.2363 71.9225 44.2597 61.8991Z" data-figma-gradient-fill="{&#34;type&#34;:&#34;GRADIENT_ANGULAR&#34;,&#34;stops&#34;:[{&#34;color&#34;:{&#34;r&#34;:0.51372551918029785,&#34;g&#34;:0.92941176891326904,&#34;b&#34;:0.79607844352722168,&#34;a&#34;:0.0},&#34;position&#34;:0.0},{&#34;color&#34;:{&#34;r&#34;:0.51372551918029785,&#34;g&#34;:0.92941176891326904,&#34;b&#34;:0.79607844352722168,&#34;a&#34;:0.69999998807907104},&#34;position&#34;:0.35000002384185791},{&#34;color&#34;:{&#34;r&#34;:0.30588236451148987,&#34;g&#34;:0.73333334922790527,&#34;b&#34;:0.59607845544815063,&#34;a&#34;:1.0},&#34;position&#34;:0.64999997615814209},{&#34;color&#34;:{&#34;r&#34;:0.0078431377187371254,&#34;g&#34;:0.43921568989753723,&#34;b&#34;:0.30196079611778259,&#34;a&#34;:1.0},&#34;position&#34;:1.0}],&#34;stopsVar&#34;:[{&#34;color&#34;:{&#34;r&#34;:0.51372551918029785,&#34;g&#34;:0.92941176891326904,&#34;b&#34;:0.79607844352722168,&#34;a&#34;:0.0},&#34;position&#34;:0.0},{&#34;color&#34;:{&#34;r&#34;:0.51372551918029785,&#34;g&#34;:0.92941176891326904,&#34;b&#34;:0.79607844352722168,&#34;a&#34;:0.69999998807907104},&#34;position&#34;:0.35000002384185791},{&#34;color&#34;:{&#34;r&#34;:0.30588236451148987,&#34;g&#34;:0.73333334922790527,&#34;b&#34;:0.59607845544815063,&#34;a&#34;:1.0},&#34;position&#34;:0.64999997615814209},{&#34;color&#34;:{&#34;r&#34;:0.0078431377187371254,&#34;g&#34;:0.43921568989753723,&#34;b&#34;:0.30196079611778259,&#34;a&#34;:1.0},&#34;position&#34;:1.0}],&#34;transform&#34;:{&#34;m00&#34;:36.298145294189453,&#34;m01&#34;:43.853122711181641,&#34;m02&#34;:-13.964984893798828,&#34;m10&#34;:-45.372684478759766,&#34;m11&#34;:35.082492828369141,&#34;m12&#34;:48.895095825195312},&#34;opacity&#34;:1.0,&#34;blendMode&#34;:&#34;NORMAL&#34;,&#34;visible&#34;:true}"/>
<g clip-path="url(#paint1_angular_9115_3108_clip_path)" data-figma-skip-parse="true"><g transform="matrix(-0.0181491 0.0222739 -0.024157 -0.0196835 44.2601 25.6011)"><foreignObject x="-1001.65" y="-1001.65" width="2003.3" height="2003.3"><div xmlns="http://www.w3.org/1999/xhtml" style="background:conic-gradient(from 90deg,rgba(148, 191, 241, 0) 0deg,rgba(148, 191, 241, 0.7) 126deg,rgba(64, 139, 227, 1) 234deg,rgba(64, 139, 227, 1) 360deg);height:100%;width:100%;opacity:1"></div></foreignObject></g></g><path d="M26.111 7.452C16.0875 17.4755 16.0875 33.7267 26.111 43.7501L44.2601 25.6011C49.0735 30.4145 51.7777 36.9429 51.7777 43.7502C51.7777 46.0145 51.4784 48.2481 50.9019 50.3979C55.1173 49.273 59.1022 47.0571 62.4091 43.7501C72.4326 33.7267 72.4326 17.4755 62.4091 7.452C52.3857 -2.57146 36.1344 -2.57146 26.111 7.452Z" data-figma-gradient-fill="{&#34;type&#34;:&#34;GRADIENT_ANGULAR&#34;,&#34;stops&#34;:[{&#34;color&#34;:{&#34;r&#34;:0.58039218187332153,&#34;g&#34;:0.74901962280273438,&#34;b&#34;:0.94509804248809814,&#34;a&#34;:0.0},&#34;position&#34;:0.0},{&#34;color&#34;:{&#34;r&#34;:0.58039218187332153,&#34;g&#34;:0.74901962280273438,&#34;b&#34;:0.94509804248809814,&#34;a&#34;:0.69999998807907104},&#34;position&#34;:0.34999999403953552},{&#34;color&#34;:{&#34;r&#34;:0.25098040699958801,&#34;g&#34;:0.54509806632995605,&#34;b&#34;:0.89019608497619629,&#34;a&#34;:1.0},&#34;position&#34;:0.64999997615814209},{&#34;color&#34;:{&#34;r&#34;:0.25098040699958801,&#34;g&#34;:0.54509806632995605,&#34;b&#34;:0.89019608497619629,&#34;a&#34;:1.0},&#34;position&#34;:1.0}],&#34;stopsVar&#34;:[{&#34;color&#34;:{&#34;r&#34;:0.58039218187332153,&#34;g&#34;:0.74901962280273438,&#34;b&#34;:0.94509804248809814,&#34;a&#34;:0.0},&#34;position&#34;:0.0},{&#34;color&#34;:{&#34;r&#34;:0.58039218187332153,&#34;g&#34;:0.74901962280273438,&#34;b&#34;:0.94509804248809814,&#34;a&#34;:0.69999998807907104},&#34;position&#34;:0.34999999403953552},{&#34;color&#34;:{&#34;r&#34;:0.25098040699958801,&#34;g&#34;:0.54509806632995605,&#34;b&#34;:0.89019608497619629,&#34;a&#34;:1.0},&#34;position&#34;:0.64999997615814209},{&#34;color&#34;:{&#34;r&#34;:0.25098040699958801,&#34;g&#34;:0.54509806632995605,&#34;b&#34;:0.89019608497619629,&#34;a&#34;:1.0},&#34;position&#34;:1.0}],&#34;transform&#34;:{&#34;m00&#34;:-36.298145294189453,&#34;m01&#34;:-48.314064025878906,&#34;m02&#34;:86.5661621093750,&#34;m10&#34;:44.547725677490234,&#34;m11&#34;:-39.367012023925781,&#34;m12&#34;:23.010715484619141},&#34;opacity&#34;:1.0,&#34;blendMode&#34;:&#34;NORMAL&#34;,&#34;visible&#34;:true}"/>
<g clip-path="url(#clip2_9115_3108)">
<path d="M159.017 54.7677L148.019 24.3583C147.754 23.4749 147.865 22.6799 148.35 21.9732C148.836 21.2223 149.543 20.8469 150.471 20.8469H152.458C153.562 20.8469 154.578 21.2003 155.506 21.9069C156.433 22.5695 157.029 23.4307 157.294 24.4908L163.456 46.155C163.456 46.1992 163.478 46.2213 163.522 46.2213C163.566 46.2213 163.588 46.1992 163.588 46.155L170.677 24.4245C171.031 23.3645 171.671 22.5032 172.599 21.8407C173.526 21.1782 174.542 20.8469 175.646 20.8469H177.236C178.164 20.8469 178.87 21.2223 179.356 21.9732C179.886 22.7241 179.975 23.5191 179.621 24.3583L163.456 66.3617C163.058 67.4218 162.396 68.2609 161.468 68.8793C160.541 69.5418 159.503 69.8731 158.354 69.8731H156.764C155.881 69.8731 155.174 69.4976 154.644 68.7468C154.159 68.0401 154.092 67.2672 154.446 66.428L159.017 55.8277C159.105 55.6511 159.149 55.4744 159.149 55.2977C159.149 55.1211 159.105 54.9444 159.017 54.7677Z" fill="#1E3E60"/>
<path d="M119.957 55.2978C118.941 55.2978 118.058 54.9224 117.307 54.1715C116.556 53.4207 116.181 52.5373 116.181 51.5215V9.38547C116.181 8.36961 116.556 7.48626 117.307 6.73541C118.058 5.98456 118.941 5.60913 119.957 5.60913H121.282C122.298 5.60913 123.181 5.98456 123.932 6.73541C124.683 7.48626 125.058 8.36961 125.058 9.38547V34.8923C125.058 34.9365 125.08 34.9586 125.125 34.9586H125.191L135.261 23.6295C136.895 21.7745 138.971 20.847 141.489 20.847H144.271C144.978 20.847 145.464 21.1782 145.729 21.8408C146.038 22.5033 145.95 23.0995 145.464 23.6295L133.34 37.0124C133.119 37.2774 133.119 37.5645 133.34 37.8736L145.596 52.449C146.082 53.0231 146.148 53.6415 145.795 54.304C145.486 54.9665 144.978 55.2978 144.271 55.2978H141.489C138.971 55.2978 136.917 54.3261 135.327 52.3827L125.191 40.1262C125.191 40.082 125.169 40.0599 125.125 40.0599C125.08 40.0599 125.058 40.082 125.058 40.1262V51.5215C125.058 52.5373 124.683 53.4207 123.932 54.1715C123.181 54.9224 122.298 55.2978 121.282 55.2978H119.957Z" fill="#1E3E60"/>
<path d="M96.9622 41.3846C92.3246 40.3687 89.012 38.9774 87.0244 37.2107C85.0811 35.444 84.1094 33.1473 84.1094 30.3206C84.1094 27.1405 85.2798 24.6671 87.6207 22.9004C90.0058 21.0895 93.5171 20.1841 98.1547 20.1841C101.07 20.1841 103.963 20.4491 106.834 20.9791C107.805 21.1558 108.578 21.6416 109.152 22.4366C109.771 23.1875 110.08 24.0708 110.08 25.0867C110.08 25.9259 109.749 26.6105 109.086 27.1405C108.424 27.6263 107.673 27.7588 106.834 27.538C104.36 26.9197 101.754 26.6105 99.016 26.6105C95.0409 26.6105 93.0533 27.7147 93.0533 29.9231C93.0533 30.9831 93.4288 31.8664 94.1796 32.5731C94.9305 33.2798 96.2113 33.8319 98.0222 34.2294C103.101 35.2453 106.569 36.6145 108.424 38.337C110.323 40.0595 111.273 42.5771 111.273 45.8897C111.273 49.0698 110.08 51.5431 107.695 53.3099C105.31 55.0766 101.843 55.9599 97.2934 55.9599C94.3342 55.9599 91.3087 55.6066 88.217 54.8999C87.2453 54.6791 86.4503 54.1711 85.8319 53.3761C85.2136 52.5369 84.9044 51.5873 84.9044 50.5273C84.9044 49.6881 85.2357 49.0477 85.8982 48.606C86.6048 48.1643 87.3557 48.0539 88.1507 48.2747C91.0216 49.0698 93.7379 49.4673 96.2997 49.4673C100.319 49.4673 102.329 48.2747 102.329 45.8897C102.329 44.7413 101.931 43.8359 101.136 43.1734C100.385 42.5109 98.9939 41.9146 96.9622 41.3846Z" fill="#1E3E60"/>
</g>
</g>
</g>
<defs>
<clipPath id="paint0_angular_9115_3108_clip_path"><path d="M44.2597 61.8991C54.2832 51.8756 54.2832 35.6244 44.2597 25.6009L26.1106 43.75C21.2972 38.9366 18.5931 32.4082 18.593 25.6009C18.593 23.3365 18.8923 21.103 19.4688 18.9532C15.2534 20.078 11.2685 22.294 7.96157 25.6009C-2.06189 35.6244 -2.06189 51.8756 7.96157 61.8991C17.985 71.9225 34.2363 71.9225 44.2597 61.8991Z"/></clipPath><clipPath id="paint1_angular_9115_3108_clip_path"><path d="M26.111 7.452C16.0875 17.4755 16.0875 33.7267 26.111 43.7501L44.2601 25.6011C49.0735 30.4145 51.7777 36.9429 51.7777 43.7502C51.7777 46.0145 51.4784 48.2481 50.9019 50.3979C55.1173 49.273 59.1022 47.0571 62.4091 43.7501C72.4326 33.7267 72.4326 17.4755 62.4091 7.452C52.3857 -2.57146 36.1344 -2.57146 26.111 7.452Z"/></clipPath><clipPath id="clip0_9115_3108">
<rect width="180" height="70" fill="white"/>
</clipPath>
<clipPath id="clip1_9115_3108">
<rect width="180.385" height="70" fill="white" transform="translate(0.311523)"/>
</clipPath>
<clipPath id="clip2_9115_3108">
<rect width="96.5865" height="64.391" fill="white" transform="translate(84.1094 5.60913)"/>
</clipPath>
</defs>
</svg>`)}`;

const decodeHtmlEntities = (value: string) => {
  if (!value) return value;

  if (typeof window !== "undefined" && typeof document !== "undefined") {
    const textarea = document.createElement("textarea");
    textarea.innerHTML = value;
    return textarea.value;
  }

  return value
    .replace(/&#(\d+);/g, (_, dec) => String.fromCharCode(Number(dec)))
    .replace(/&#x([\da-f]+);/gi, (_, hex) =>
      String.fromCharCode(parseInt(hex, 16))
    )
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/<br\s*\/?>/gi, "\n");
};

export const createPdfDocumentFromMarkdown = (
  markdown: string,
  {
    title,
    includeTitle = true,
    titleAlignment = "left",
    showHeader = false,
    caseInfo,
    includeFooter = false,
    includeLogo = true,
    logoSrc,
    fontFamily,
    lineSpacing,
    footnote,
  }: PdfDocumentOptions
) => {
  const footerPadding = includeFooter ? 80 : 40;
  const resolvedLogoSrc = includeLogo ? logoSrc || logoDataUrl : undefined;

  const exportStyles = getExportStyles(fontFamily, lineSpacing);
  const { sizes, spacing } = exportStyles;
  const listSpacing = spacing.list;

  const pdfFontMap: Record<
    string,
    { body: string; bold: string; italic: string }
  > = {
    sans: {
      body: "Helvetica",
      bold: "Helvetica-Bold",
      italic: "Helvetica-Oblique",
    },
    serif: { body: "Times-Roman", bold: "Times-Bold", italic: "Times-Italic" },
    mono: { body: "Courier", bold: "Courier-Bold", italic: "Courier-Oblique" },
  };

  const pdfFonts =
    fontFamily && pdfFontMap[fontFamily]
      ? pdfFontMap[fontFamily]
      : { body: "Times-Roman", bold: "Times-Bold", italic: "Times-Italic" };

  const styles = StyleSheet.create({
    page: {
      paddingTop: 32,
      paddingBottom: footerPadding,
      paddingHorizontal: 32,
      fontSize: sizes.body,
      lineHeight: exportStyles.lineHeight,
      fontFamily: pdfFonts.body,
      position: "relative",
    },
    content: {
      width: "100%",
    },
    title: {
      fontSize: sizes.h1,
      marginBottom: spacing.h1.after,
      fontFamily: pdfFonts.bold,
      textAlign: "left",
    },
    titleCentered: {
      fontSize: sizes.h1,
      marginBottom: spacing.h1.after,
      fontFamily: pdfFonts.bold,
      textAlign: "center",
    },
    header: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "flex-start",
      marginBottom: 14,
    },
    headerLeft: {
      flex: 1,
      paddingRight: 12,
    },
    caseInfo: {
      fontSize: 11,
      lineHeight: 1.4,
      color: "#71717A",
      maxWidth: "70%",
      fontFamily: pdfFonts.body,
    },
    caseInfoLine: {
      marginBottom: 2,
      lineHeight: exportStyles.lineHeight,
    },
    logo: {
      width: 100,
      height: 40,
      objectFit: "contain",
    },
    footer: {
      position: "absolute",
      left: 24,
      right: 24,
      bottom: 24,
      flexDirection: "row",
      justifyContent: "space-between",
      fontSize: 9,
      color: "#71717A",
      fontFamily: pdfFonts.body,
    },
    footerText: {
      fontFamily: pdfFonts.body,
      color: "#71717A",
      textDecoration: "none",
    },
    paragraph: {
      marginBottom: spacing.paragraph.after,
      fontFamily: pdfFonts.body,
      fontSize: sizes.body,
      lineHeight: exportStyles.lineHeight,
    },
    heading1: {
      fontSize: sizes.h1,
      marginTop: spacing.h1.before,
      marginBottom: spacing.h1.after,
      fontFamily: pdfFonts.bold,
      lineHeight: 1.4,
    },
    heading2: {
      fontSize: sizes.h2,
      marginTop: spacing.h2.before,
      marginBottom: spacing.h2.after,
      fontFamily: pdfFonts.bold,
      lineHeight: 1.4,
    },
    heading3: {
      fontSize: sizes.h3,
      marginTop: spacing.h3.before,
      marginBottom: spacing.h3.after,
      fontFamily: pdfFonts.bold,
      lineHeight: 1.35,
    },
    heading4: {
      fontSize: sizes.h4,
      marginTop: spacing.h4.before,
      marginBottom: spacing.h4.after,
      fontFamily: pdfFonts.bold,
      lineHeight: 1.3,
    },
    heading5: {
      fontSize: sizes.h5,
      marginTop: spacing.h5.before,
      marginBottom: spacing.h5.after,
      fontFamily: pdfFonts.bold,
      lineHeight: 1.3,
    },
    heading6: {
      fontSize: sizes.h6,
      marginTop: spacing.h6.before,
      marginBottom: spacing.h6.after,
      fontFamily: pdfFonts.bold,
      lineHeight: 1.2,
    },
    blockquoteContainer: {
      marginTop: spacing.paragraph.after / 2,
      marginBottom: spacing.paragraph.after,
      marginLeft: 16,
      paddingLeft: 12,
      borderLeftWidth: 3,
      borderLeftColor: "#D4D4D8",
      backgroundColor: "#F9FAFB",
      paddingTop: 6,
      paddingBottom: 6,
    },
    blockquoteText: {
      fontFamily: pdfFonts.italic,
      fontSize: sizes.body,
      color: "#4B5563",
      lineHeight: 1.4,
    },
    codeBlockContainer: {
      marginTop: spacing.paragraph.after / 2,
      marginBottom: spacing.paragraph.after / 2,
      paddingLeft: 12,
      paddingRight: 12,
      paddingTop: 10,
      paddingBottom: 10,
      backgroundColor: "#1F2937",
      borderRadius: 6,
    },
    codeBlockText: {
      fontFamily: pdfFonts.body,
      fontSize: sizes.code,
      color: "#FFFFFF",
      lineHeight: 1.4,
    },
    list: {
      marginTop: listSpacing.before,
      marginBottom: listSpacing.after,
      paddingLeft: 0,
    },
    listItem: {
      marginBottom: listSpacing.itemGap,
      flexDirection: "row",
      alignItems: "flex-start",
    },
    bullet: {
      marginRight: 6,
      fontFamily: pdfFonts.bold,
      fontSize: sizes.body,
    },
    listParagraph: {
      fontFamily: pdfFonts.body,
      fontSize: sizes.body,
      lineHeight: exportStyles.lineHeight,
      marginBottom: 0,
    },
    defTerm: {
      fontWeight: "bold",
      marginTop: 6,
      marginBottom: 2,
      fontFamily: pdfFonts.bold,
    },
    defDescription: {
      marginLeft: 20,
      marginBottom: 4,
      fontFamily: pdfFonts.body,
    },
  });

  // Convert HTML lists and inline formatting to markdown before lexing,
  // since marked treats inline HTML tags as individual tokens and renders them as raw text.
  const convertHtmlToMarkdown = (text: string): string => {
    let result = text;
    // Convert inline formatting to markdown equivalents
    result = result.replace(/<(b|strong)\b[^>]*>([\s\S]*?)<\/\1>/gi, "**$2**");
    result = result.replace(/<(i|em)\b[^>]*>([\s\S]*?)<\/\1>/gi, "*$2*");
    // Convert <ul>/<ol> lists to markdown lists
    result = result.replace(
      /<(ul|ol)\b[^>]*>([\s\S]*?)<\/\1>/gi,
      (_match, tag: string, inner: string) => {
        const isOrdered = tag.toLowerCase() === "ol";
        const items = [...inner.matchAll(/<li\b[^>]*>([\s\S]*?)<\/li>/gi)];
        if (items.length === 0) return inner;
        return (
          "\n\n" +
          items
            .map((m, i) => `${isOrdered ? `${i + 1}.` : "-"} ${m[1].trim()}`)
            .join("\n") +
          "\n\n"
        );
      }
    );
    return result;
  };

  const cleanedMarkdown = convertHtmlToMarkdown(
    ensureBoldHeadingSpacing(
      ensureMarkdownNewlines(
        preprocessFootnotes(normalizeLiteralNewlines(markdown))
      )
    ).replace(/<\/?br\s*\/?>/gi, "  \n")
  );
  const tokens = marked.lexer(cleanedMarkdown);
  const elements: any[] = [];
  const h = React.createElement;

  const pushText = (key: string, text: string, style: any) => {
    if (!text) return;
    const decoded = decodeHtmlEntities(text);
    if (!decoded) return;
    elements.push(h(Text, { key, style }, decoded));
  };

  const isBoldFamily = (family?: string) =>
    Boolean(family && (family.includes("Bold") || family.includes("bold")));
  const isItalicFamily = (family?: string) =>
    Boolean(
      family &&
      (family.includes("Italic") ||
        family.includes("Oblique") ||
        family.includes("italic"))
    );

  const resolveFontFamily = (
    baseFamily: string | undefined,
    bold: boolean,
    italic: boolean
  ) => {
    const family = baseFamily || "Times-Roman";
    const root = family.includes("Helvetica")
      ? "Helvetica"
      : family.includes("Courier")
        ? "Courier"
        : "Times";

    const variants = {
      Times: {
        normal: "Times-Roman",
        bold: "Times-Bold",
        italic: "Times-Italic",
        boldItalic: "Times-BoldItalic",
      },
      Helvetica: {
        normal: "Helvetica",
        bold: "Helvetica-Bold",
        italic: "Helvetica-Oblique",
        boldItalic: "Helvetica-BoldOblique",
      },
      Courier: {
        normal: "Courier",
        bold: "Courier-Bold",
        italic: "Courier-Oblique",
        boldItalic: "Courier-BoldOblique",
      },
    } as const;

    const variantKey =
      bold && italic
        ? "boldItalic"
        : bold
          ? "bold"
          : italic
            ? "italic"
            : "normal";

    return variants[root][variantKey];
  };

  const renderInlineToPdfTexts = (
    inlineTokens: any[] | undefined,
    baseStyle: any,
    keyPrefix: string
  ) => {
    if (!inlineTokens || !Array.isArray(inlineTokens)) return [];
    const out: any[] = [];
    let idx = 0;
    const hLocal = React.createElement;

    const emitText = (text: string, style: any) => {
      if (!text) return;
      const decoded = decodeHtmlEntities(text);
      if (!decoded) return;
      out.push(hLocal(Text, { key: `${keyPrefix}-${idx++}`, style }, decoded));
    };

    const mergeStyle = (style: any, token: any) => {
      const existingFamily = style?.fontFamily;
      let bold = isBoldFamily(existingFamily) || style?.fontWeight === "bold";
      let italic =
        isItalicFamily(existingFamily) || style?.fontStyle === "italic";

      const s = { ...style };

      if (token.type === "strong") {
        bold = true;
      }

      if (token.type === "em") {
        italic = true;
      }

      if (token.type === "codespan") {
        return {
          ...s,
          fontFamily: "Times-Roman",
          fontSize: sizes.code,
          backgroundColor: "#1F2937",
          color: "#FFFFFF",
          paddingLeft: 5,
          paddingRight: 5,
          paddingTop: 2,
          paddingBottom: 2,
          borderRadius: 6,
        };
      }

      if (token.type === "del") {
        s.textDecoration = "line-through";
      }

      if (token.type === "link") {
        s.color = "#71717A";
        s.textDecoration = "none";
        s.fontSize = sizes.link;
      }

      s.fontFamily = resolveFontFamily(existingFamily, bold, italic);
      s.fontWeight = bold ? "bold" : "normal";
      s.fontStyle = italic ? "italic" : "normal";

      return s;
    };

    let underlineDepth = 0;

    const walk = (token: any, parentStyle: any) => {
      if (token.type === "link") {
        const href = token.href || "";
        const linkStyle = mergeStyle(parentStyle, token);
        const rawText = token.text;
        const displayText = /^\d+$/.test(rawText) ? `[${rawText}]` : rawText;
        out.push(
          hLocal(
            Link,
            {
              key: `${keyPrefix}-${idx++}`,
              src: href,
              style: linkStyle,
            },
            displayText
          )
        );
        return;
      }

      if (token.type === "html") {
        const htmlTag = token.text || token.raw || "";
        const tagMatch = htmlTag.match(/<\/?([a-z]+)[^>]*>/i);

        if (tagMatch) {
          const tagName = tagMatch[1].toLowerCase();
          if (tagName === "u") {
            if (htmlTag.match(/^<u\b/i)) {
              underlineDepth++;
            } else {
              underlineDepth = Math.max(0, underlineDepth - 1);
            }
            return;
          }
          if (
            ["b", "strong", "i", "em", "code", "a", "ul", "ol", "li"].includes(
              tagName
            )
          ) {
            return;
          }
        }
        emitText(htmlTag, parentStyle);
        return;
      }

      let style = mergeStyle(parentStyle, token);
      if (underlineDepth > 0) {
        const existing = style.textDecoration;
        style = {
          ...style,
          textDecoration:
            existing && !existing.includes("underline")
              ? `${existing} underline`
              : existing || "underline",
        };
      }
      if (token.tokens && Array.isArray(token.tokens)) {
        token.tokens.forEach((t: any) => walk(t, style));
        return;
      }
      if (token.type === "text") {
        emitText(token.text, style);
        return;
      }
      if (typeof token.raw === "string") {
        emitText(token.raw, style);
      } else if (typeof token.text === "string") {
        emitText(token.text, style);
      }
    };

    inlineTokens.forEach((t) => walk(t, baseStyle));
    return out;
  };

  let heading1Count = 0;
  let heading2Count = 0;
  let heading3Count = 0;
  let heading4Count = 0;
  let heading5Count = 0;
  let heading6Count = 0;

  let paraCount = 0;
  let listCount = 0;
  let liCount = 0;
  let defListCount = 0;
  let defTermCount = 0;
  let defDescCount = 0;
  let hrCount = 0;
  let tableCount = 0;

  for (const token of tokens as any[]) {
    switch (token.type) {
      case "heading": {
        let style = styles.paragraph;
        let key = `hn-${paraCount++}`;
        switch (token.depth) {
          case 1:
            style = styles.heading1;
            key = `h1-${heading1Count++}`;
            break;
          case 2:
            style = styles.heading2;
            key = `h2-${heading2Count++}`;
            break;
          case 3:
            style = styles.heading3;
            key = `h3-${heading3Count++}`;
            break;
          case 4:
            style = styles.heading4;
            key = `h4-${heading4Count++}`;
            break;
          case 5:
            style = styles.heading5;
            key = `h5-${heading5Count++}`;
            break;
          case 6:
            style = styles.heading6;
            key = `h6-${heading6Count++}`;
            break;
          default:
            break;
        }

        const inlineParts = renderInlineToPdfTexts(
          (token as any).tokens,
          style,
          key
        );
        if (inlineParts.length) {
          elements.push(h(Text, { key, style }, ...inlineParts));
        } else {
          pushText(key, token.text, style);
        }
        break;
      }
      case "paragraph": {
        const key = `p-${paraCount++}`;
        const allTokens: any[] = (token as any).tokens || [];
        const imageTokens = allTokens.filter((t: any) => t.type === "image");
        const nonImageTokens = allTokens.filter((t: any) => t.type !== "image");

        if (nonImageTokens.length > 0) {
          const inlineParts = renderInlineToPdfTexts(
            nonImageTokens,
            styles.paragraph,
            key
          );
          if (inlineParts.length) {
            elements.push(
              h(Text, { key, style: styles.paragraph }, ...inlineParts)
            );
          } else {
            pushText(key, token.text, styles.paragraph);
          }
        }

        imageTokens.forEach((imgToken: any) => {
          const imgKey = `img-${paraCount++}`;
          const imgSrc = imgToken.href || "";
          if (imgSrc) {
            elements.push(
              h(
                View,
                {
                  key: imgKey,
                  style: {
                    marginBottom: spacing.paragraph.after,
                    alignItems: "center",
                  },
                },
                h(PdfImage, {
                  src: imgSrc,
                  style: {
                    maxWidth: "100%",
                    maxHeight: 400,
                    objectFit: "contain",
                  },
                })
              )
            );
          } else {
            const altText = imgToken.text || "[Image]";
            pushText(imgKey, altText, styles.paragraph);
          }
        });
        break;
      }
      case "list": {
        const renderList = (
          listToken: any,
          depth: number,
          keyPrefix: string
        ): any => {
          const items: any[] = listToken.items || [];
          // Honor the ordered list `start` attribute from the markdown token
          // so that lists authored with explicit non-1 starts (e.g. `25.`
          // continuing after a heading) keep their numbering instead of
          // resetting to `1.` for every new list block.
          const startNumber =
            listToken.ordered && typeof listToken.start === "number"
              ? listToken.start
              : 1;
          const children: any[] = [];
          for (let i = 0; i < items.length; i++) {
            const item = items[i] || {};
            let prefix = listToken.ordered ? `${startNumber + i}.` : "\u2022";

            if (item.task) {
              prefix = item.checked ? "☑" : "☐";
            }

            const k = `${keyPrefix}-li-${liCount++}`;

            // Separate inline tokens from nested list tokens
            const inlineTokens = (item.tokens || []).filter(
              (t: any) => t.type !== "list"
            );
            const nestedLists = (item.tokens || []).filter(
              (t: any) => t.type === "list"
            );

            const inlineParts = renderInlineToPdfTexts(
              inlineTokens,
              styles.listParagraph,
              k
            );
            const bulletRun = h(Text, { style: styles.bullet }, `${prefix} `);
            const content =
              inlineParts.length > 0
                ? h(
                    Text,
                    { style: styles.listParagraph },
                    bulletRun,
                    ...inlineParts
                  )
                : h(
                    Text,
                    { style: styles.listParagraph },
                    bulletRun,
                    decodeHtmlEntities(
                      (item.text || "").replace(/^\s*[-*+]\s+/, "")
                    )
                  );

            const itemChildren: any[] = [];

            if (listToken.ordered && depth === 0) {
              itemChildren.push(
                h(Text, {
                  key: `${k}-anchor`,
                  id: `ref-${startNumber + i}`,
                  style: { fontSize: 0, lineHeight: 0 },
                })
              );
            }

            itemChildren.push(content);

            // Recursively render nested lists
            for (let j = 0; j < nestedLists.length; j++) {
              itemChildren.push(
                renderList(nestedLists[j], depth + 1, `${k}-n${j}`)
              );
            }

            children.push(
              h(
                View,
                {
                  key: k,
                  style: {
                    ...styles.listItem,
                    flexDirection: "column",
                  },
                  wrap: true,
                },
                ...itemChildren
              )
            );
          }
          return h(
            View,
            {
              key: `list-${listCount++}`,
              style: {
                ...styles.list,
                ...(depth > 0
                  ? { marginTop: 0, marginBottom: 0, paddingLeft: 12 }
                  : {}),
              },
              wrap: true,
            },
            ...children
          );
        };

        elements.push(renderList(token, 0, `l${listCount}`));
        break;
      }
      case "blockquote": {
        const key = `bq-${paraCount++}`;
        const inlineParts = renderInlineToPdfTexts(
          (token as any).tokens,
          styles.blockquoteText,
          `${key}-inline`
        );

        const content = inlineParts.length
          ? h(Text, { style: styles.blockquoteText }, ...inlineParts)
          : h(
              Text,
              { style: styles.blockquoteText },
              decodeHtmlEntities(token.text || "")
            );

        elements.push(
          h(
            View,
            { key, style: styles.blockquoteContainer, wrap: true },
            content
          )
        );
        break;
      }
      case "code": {
        const key = `code-${paraCount++}`;
        const codeText = (token.text || "").replace(/\t/g, "    ");
        elements.push(
          h(
            View,
            { key, style: styles.codeBlockContainer, wrap: false },
            h(Text, { style: styles.codeBlockText }, codeText)
          )
        );
        break;
      }
      case "hr": {
        elements.push(
          h(View, {
            key: `hr-${hrCount++}`,
            style: {
              width: "100%",
              height: 1,
              backgroundColor: "#CCCCCC",
              marginTop: 8,
              marginBottom: 8,
            },
          })
        );
        break;
      }
      case "table": {
        const tableData = token.header || [];
        const tableRows = token.rows || [];

        // If the first header cell is "#", this is a Resources table.
        // Push zero-height anchor <Text> elements directly into `elements`
        // (siblings of the table, not nested inside it) so that react-pdf's
        // named-destination Y is relative to the content view, not a table
        // cell. All anchors are stacked at the same spot (top of the table),
        // so any #ref-N link navigates to the Resources section.
        const firstHeaderText = (tableData[0]?.text || "").trim();
        if (firstHeaderText === "#") {
          tableRows.forEach((row: any) => {
            const firstCellText = (row[0]?.text || "").trim();
            if (/^\d+$/.test(firstCellText)) {
              elements.push(
                h(Text, {
                  key: `anchor-ref-${firstCellText}-${tableCount}`,
                  id: `ref-${firstCellText}`,
                  style: { fontSize: 0, lineHeight: 0 },
                })
              );
            }
          });
        }

        const tableStyle = {
          width: "100%",
          marginBottom: 12,
          borderWidth: 1,
          borderColor: "#CCCCCC",
          borderStyle: "solid" as const,
        };

        const cellBaseStyle = {
          flex: 1,
          padding: 4,
          borderRightWidth: 1,
          borderRightColor: "#CCCCCC",
        };

        const headerCellStyle = {
          ...cellBaseStyle,
          backgroundColor: "#F0F0F0",
        };

        const tableChildren: any[] = [];

        if (tableData.length > 0) {
          const headerCells = tableData.map((cell: any, idx: number) => {
            const headerTextStyle = {
              fontSize: 10,
              fontFamily: pdfFonts.bold,
              fontWeight: "bold" as const,
            };
            const inlineContent = cell.tokens
              ? renderInlineToPdfTexts(
                  cell.tokens,
                  headerTextStyle,
                  `th-inline-${tableCount}-${idx}`
                )
              : [decodeHtmlEntities(cell.text || "")];
            return h(
              View,
              {
                key: `th-${tableCount}-${idx}`,
                style: headerCellStyle,
              },
              h(
                Text,
                {
                  style: headerTextStyle,
                },
                ...inlineContent
              )
            );
          });
          tableChildren.push(
            h(
              View,
              {
                key: `thead-${tableCount}`,
                style: { flexDirection: "row", width: "100%" },
              },
              ...headerCells
            )
          );
        }

        tableRows.forEach((row: any, rowIdx: number) => {
          const rowCells = (row || []).map((cell: any, cellIdx: number) => {
            const cellTextStyle = {
              fontSize: 10,
              fontFamily: pdfFonts.body,
            };
            const inlineContent = cell.tokens
              ? renderInlineToPdfTexts(
                  cell.tokens,
                  cellTextStyle,
                  `td-inline-${tableCount}-${rowIdx}-${cellIdx}`
                )
              : [decodeHtmlEntities(cell.text || "")];
            return h(
              View,
              {
                key: `td-${tableCount}-${rowIdx}-${cellIdx}`,
                style: cellBaseStyle,
              },
              h(Text, { style: cellTextStyle }, ...inlineContent)
            );
          });
          tableChildren.push(
            h(
              View,
              {
                key: `tr-${tableCount}-${rowIdx}`,
                style: { flexDirection: "row", width: "100%" },
              },
              ...rowCells
            )
          );
        });

        if (tableChildren.length > 0) {
          elements.push(
            h(
              View,
              {
                key: `table-${tableCount++}`,
                style: tableStyle,
              },
              ...tableChildren
            )
          );
        }
        break;
      }
      case "image": {
        const imgKey = `img-${paraCount++}`;
        const imgSrc = (token as { href?: string }).href || "";
        if (imgSrc) {
          elements.push(
            h(
              View,
              {
                key: imgKey,
                style: {
                  marginBottom: spacing.paragraph.after,
                  alignItems: "center",
                },
              },
              h(PdfImage, {
                src: imgSrc,
                style: {
                  maxWidth: "100%",
                  maxHeight: 400,
                  objectFit: "contain",
                },
              })
            )
          );
        } else {
          const altText = (token as { text?: string }).text || "[Image]";
          pushText(imgKey, altText, styles.paragraph);
        }
        break;
      }
      case "space": {
        // Ignore space tokens — spacing is controlled by margins on actual elements
        break;
      }
      case "html": {
        if (token.text && token.text.includes("<dl>")) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(token.text, "text/html");
          const dlElements = doc.querySelectorAll("dl");

          dlElements.forEach((dl) => {
            const defListChildren: any[] = [];

            Array.from(dl.children).forEach((child) => {
              const tagName = child.tagName.toLowerCase();

              if (tagName === "dt") {
                defListChildren.push(
                  h(
                    Text,
                    {
                      key: `dt-${defListCount}-${defTermCount++}`,
                      style: styles.defTerm,
                    },
                    child.textContent || ""
                  )
                );
              } else if (tagName === "dd") {
                defListChildren.push(
                  h(
                    Text,
                    {
                      key: `dd-${defListCount}-${defDescCount++}`,
                      style: styles.defDescription,
                    },
                    child.textContent || ""
                  )
                );
              }
            });

            if (defListChildren.length > 0) {
              elements.push(
                h(
                  View,
                  { key: `dl-${defListCount++}`, wrap: true },
                  ...defListChildren
                )
              );
            }
          });
        } else if (token.text) {
          pushText(`html-${paraCount++}`, token.text, styles.paragraph);
        }
        break;
      }
      default: {
        if (token.text)
          pushText(`t-${paraCount++}`, token.text, styles.paragraph);
      }
    }
  }

  const pageChildren: any[] = [];

  const shouldRenderHeader = showHeader || Boolean(resolvedLogoSrc);

  if (shouldRenderHeader) {
    const caseInfoLines: string[] = [];
    if (caseInfo?.caseName) {
      caseInfoLines.push(`Case Name: ${caseInfo.caseName}`);
    }
    if (caseInfo?.dateOfBirth && caseInfo.dateOfBirth !== "Invalid Date") {
      caseInfoLines.push(`Date of Birth: ${caseInfo.dateOfBirth}`);
    }
    if (caseInfo?.dateOfLoss && caseInfo.dateOfLoss !== "Invalid Date") {
      caseInfoLines.push(`Date of Loss: ${caseInfo.dateOfLoss}`);
    }
    if (caseInfo?.claimNumber) {
      caseInfoLines.push(`Claim/Case #: ${caseInfo.claimNumber}`);
    }

    const caseInfoView = caseInfoLines.length
      ? h(
          View,
          { key: "case-info", style: styles.caseInfo },
          ...caseInfoLines.map((line, idx) =>
            h(
              Text,
              { key: `case-line-${idx}`, style: styles.caseInfoLine },
              line
            )
          )
        )
      : null;

    const leftColumn = h(
      View,
      { key: "header-left", style: styles.headerLeft },
      caseInfoView
    );

    const headerChildren: React.ReactNode[] = [leftColumn];

    if (resolvedLogoSrc) {
      headerChildren.push(
        h(PdfImage, { key: "logo", style: styles.logo, src: resolvedLogoSrc })
      );
    }

    pageChildren.push(
      h(View, { key: "header", style: styles.header }, ...headerChildren)
    );
  }

  if (includeTitle) {
    const titleStyle =
      titleAlignment === "center" ? styles.titleCentered : styles.title;
    pageChildren.push(h(Text, { key: "title", style: titleStyle }, title));
  }

  pageChildren.push(
    h(View, { key: "content", style: styles.content, wrap: true }, ...elements)
  );

  if (includeFooter) {
    const footerText = footnote || "Powered by Sky AI";
    pageChildren.push(
      h(
        View,
        { key: "footer", style: styles.footer, fixed: true },
        footnote
          ? h(Text, { style: styles.footerText }, footerText)
          : h(
              Link,
              { src: "https://www.usesky.ai/", style: styles.footerText },
              footerText
            ),
        h(
          Text,
          {
            style: styles.footerText,
            render: ({ pageNumber, totalPages }: any) =>
              `${pageNumber} of ${totalPages}`,
          },
          ""
        )
      )
    );
  }

  return h(
    PdfDocument,
    null,
    h(Page, { size: "A4", style: styles.page, wrap: true }, ...pageChildren)
  );
};
